import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Search,
  Eye,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Pencil,
} from "lucide-react";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  institutionsAPI,
  type Institution as ApiInstitution,
} from "../../services/api";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../contexts/AuthContext";
import { getUserInfo } from "@/lib/storeManager";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { INSTITUTION_ONBOARDING_STEPS } from "../../constants/institutionFormFields";
import InstitutionOnboardingForm from "../../components/forms/InstitutionOnboardingForm";

interface Institution {
  id: string | number;
  name: string;
  type: string | null;
  city: string | null;
  state: string | null;
  status: string | null;
  created_at: string;
  trustee_name: string | null;
  is_highlighted?: boolean;
}

interface InstitutionStatistics {
  total_institutions: number;
  pending_approval: number;
  approved: number;
  rejected: number;
}

const mapResultToInstitution = (
  item: ApiInstitution & { approval_status?: string; is_highlighted?: boolean }
): Institution => ({
  id: item.id,
  name: item.name ?? "",
  type: item.type ?? null,
  city: item.city ?? null,
  state: item.state ?? null,
  status: item.approval_status ?? item.status ?? null,
  created_at: item.created_at ?? "",
  trustee_name: item.trustee_name ?? null,
  is_highlighted: item.is_highlighted ?? false,
});

export default function Institutions() {
  const navigate = usePrefixedNavigate();
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [statistics, setStatistics] = useState<InstitutionStatistics | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [highlightUpdating, setHighlightUpdating] = useState<
    string | number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstitutionId, setEditingInstitutionId] = useState<
    string | number | null
  >(null);

  // Institution form state (for Add/Edit modal)
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [initialData, setInitialData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [institutionImages, setInstitutionImages] = useState<File[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const MAX_INSTITUTION_IMAGES = 15;
  const form = useForm();

  const { user: authUser } = useAuth();
  const userInfo = getUserInfo();
  const jbfRole = authUser?.jbf_role ?? userInfo?.jbf_role ?? null;
  const isSuperAdmin = jbfRole === "super_admin";

  const handleHighlightChange = async (inst: Institution, value: boolean) => {
    setHighlightUpdating(inst.id);
    try {
      await institutionsAPI.setHighlighted(inst.id, value);
      setInstitutions(prev =>
        prev.map(i => (i.id === inst.id ? { ...i, is_highlighted: value } : i))
      );
      toast.success(value ? "Institution highlighted" : "Highlight removed");
    } catch (e: any) {
      console.error("Failed to update highlight:", e);
      toast.error("Failed to update highlight");
    } finally {
      setHighlightUpdating(null);
    }
  };

  const fetchInstitutions = useCallback(
    async (params?: { search?: string; approval_status?: string }) => {
      setLoading(true);
      try {
        const raw = await institutionsAPI.getApprovalsAll(params);
        // API returns { statistics: {...}, results: [...] }
        let list: Institution[] = [];
        let stats: InstitutionStatistics | null = null;

        if (raw && typeof raw === "object") {
          const obj = raw as {
            statistics?: InstitutionStatistics;
            results?: (ApiInstitution & {
              approval_status?: string;
              is_highlighted?: boolean;
            })[];
            data?: {
              statistics?: InstitutionStatistics;
              results?: (ApiInstitution & {
                approval_status?: string;
                is_highlighted?: boolean;
              })[];
            };
          };
          const payload = obj.data ?? obj;
          if (payload.statistics) {
            stats = payload.statistics;
          }
          if (Array.isArray(payload.results)) {
            list = payload.results.map(mapResultToInstitution);
          }
        } else if (Array.isArray(raw)) {
          list = (raw as (ApiInstitution & { approval_status?: string })[]).map(
            mapResultToInstitution
          );
        }

        setStatistics(stats);
        setInstitutions(list);
      } catch (error: any) {
        console.error("Error fetching institutions:", error);
        toast.error("Failed to fetch institutions");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const params: { search?: string; approval_status?: string } = {};
      const trimmedSearch = searchTerm.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      if (statusFilter !== "all") {
        params.approval_status = statusFilter;
      }
      fetchInstitutions(params);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, fetchInstitutions]);

  // Load institution data when editing in modal — normalize and set all fields including Capacity & Facilities
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    if (editingInstitutionId) {
      setLoadingFormData(true);
      institutionsAPI
        .get(String(editingInstitutionId))
        .then((data: any) => {
          if (!data || typeof data !== "object") {
            return;
          }
          setInitialData(data);
          // Normalize API keys (support both snake_case and camelCase from API)
          const normalized: Record<string, unknown> = { ...data };
          if (data.total_students !== null) {
            normalized.total_students = data.total_students;
          }
          if (data.teaching_staff !== null) {
            normalized.teaching_staff = data.teaching_staff;
          }
          if (data.non_teaching_staff !== null) {
            normalized.non_teaching_staff = data.non_teaching_staff;
          }
          if (data.classrooms !== null) {
            normalized.classrooms = data.classrooms;
          }
          if (data.campus_area !== null) {
            normalized.campus_area = data.campus_area;
          }
          setFormData(normalized);
          form.reset(normalized);
          const depts = data.departments ?? data.selected_departments;
          if (Array.isArray(depts)) {
            setSelectedDepartments(depts);
          } else if (typeof depts === "string") {
            setSelectedDepartments(depts ? [depts] : []);
          } else {
            setSelectedDepartments([]);
          }
          const facs = data.facilities ?? data.selected_facilities;
          if (Array.isArray(facs)) {
            setSelectedFacilities(facs);
          } else if (typeof facs === "string") {
            setSelectedFacilities(facs ? [facs] : []);
          } else {
            setSelectedFacilities([]);
          }
          const lvls = data.levels ?? data.selected_levels;
          if (Array.isArray(lvls)) {
            setSelectedLevels(lvls);
          } else if (typeof lvls === "string") {
            setSelectedLevels(lvls ? [lvls] : []);
          }
          if (data.boarding_gender !== null) {
            form.setValue("boarding_gender", String(data.boarding_gender));
          }
          if (
            Array.isArray(data.educational_year) &&
            data.educational_year.length > 0
          ) {
            form.setValue(
              "educational_year",
              data.educational_year.map((item: any) => ({
                educational_year: String(item?.educational_year ?? ""),
                total_capacity: Number(item?.total_capacity ?? 0),
                vacant: Number(item?.vacant ?? 0),
                occupied: Number(item?.occupied ?? 0),
              }))
            );
          }
          // Existing images from API (images array of URLs) for display in edit
          const imagesArr = data.images ?? data.image;
          if (Array.isArray(imagesArr) && imagesArr.length > 0) {
            setExistingImageUrls(
              imagesArr
                .map((u: string | { url?: string }) =>
                  typeof u === "string" ? u : (u?.url ?? "")
                )
                .filter(Boolean)
            );
          } else {
            setExistingImageUrls([]);
          }
        })
        .catch(() => {
          toast.error("Failed to load institution data");
        })
        .finally(() => setLoadingFormData(false));
    } else {
      setStep(1);
      setFormData({});
      setInitialData(null);
      form.reset({});
      setSelectedDepartments([]);
      setSelectedFacilities([]);
      setSelectedLevels([]);
      setInstitutionImages([]);
      setExistingImageUrls([]);
    }
  }, [isModalOpen, editingInstitutionId, form]);

  // Reset form state when modal closes
  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      setStep(1);
      setFormData({});
      setInitialData(null);
      form.reset({});
      setSelectedDepartments([]);
      setSelectedFacilities([]);
      setSelectedLevels([]);
      setInstitutionImages([]);
      setExistingImageUrls([]);
    }
    setIsModalOpen(open);
  };

  const handleFormNext = (data: Record<string, unknown>) => {
    setFormData(prev => {
      const merged = { ...prev, ...data };
      form.reset(merged);
      return merged;
    });
    setStep(s => s + 1);
  };

  const handleFormBack = () => {
    setStep(s => s - 1);
    form.reset(formData);
  };

  const handleFormFinalSubmit = async (data: any) => {
    const rawEducationalYear =
      formData.educational_year ?? data.educational_year;
    const educational_year = Array.isArray(rawEducationalYear)
      ? rawEducationalYear.map((item: Record<string, unknown>) => ({
          educational_year: String(item?.educational_year ?? ""),
          total_capacity: Number(item?.total_capacity ?? 0),
          vacant: Number(item?.vacant ?? 0),
          occupied: Number(item?.occupied ?? 0),
        }))
      : [];
    // Merge formData + current form values (step 5) + form.getValues() so Capacity & Facilities (step 4) and all steps are in payload
    const currentFormValues = form.getValues();
    const finalData = {
      ...formData,
      ...currentFormValues,
      ...data,
      educational_year,
      selectedDepartments,
      selectedFacilities,
      selectedLevels,
      image: institutionImages,
    };

    let payloadToSubmit: Record<string, any> = finalData;

    try {
      let result: any;
      if (editingInstitutionId) {
        if (initialData) {
          payloadToSubmit = {};
          // Only pass updated field values
          for (const key of Object.keys(finalData)) {
            const val = finalData[key as keyof typeof finalData];
            let origVal = initialData[key];

            // Handle mismatched key names between frontend state and backend response for arrays
            if (key === "selectedDepartments") {
              origVal =
                initialData.departments ?? initialData.selected_departments;
            } else if (key === "selectedFacilities") {
              origVal =
                initialData.facilities ?? initialData.selected_facilities;
            } else if (key === "selectedLevels") {
              origVal = initialData.levels ?? initialData.selected_levels;
            }

            // Always pass arrays if changed to ensure proper form data mappings
            if (
              key === "educational_year" ||
              key === "selectedDepartments" ||
              key === "selectedFacilities" ||
              key === "selectedLevels" ||
              key === "image"
            ) {
              // Compare arrays properly
              const strVal = JSON.stringify(Array.isArray(val) ? val : []);
              const strOrig = JSON.stringify(
                Array.isArray(origVal) ? origVal : []
              );
              if (strVal !== strOrig || key === "image") {
                payloadToSubmit[key] = val;
              }
              continue;
            }

            // Send if modified string/number/boolean value
            if (String(val ?? "") !== String(origVal ?? "")) {
              payloadToSubmit[key] = val;
            }
          }
        }

        if (institutionImages.length > 0) {
          result = await institutionsAPI.updateWithImages(
            String(editingInstitutionId),
            payloadToSubmit,
            institutionImages
          );
        } else {
          result = await institutionsAPI.update(
            String(editingInstitutionId),
            payloadToSubmit
          );
        }
      } else {
        result = await institutionsAPI.create(
          payloadToSubmit,
          institutionImages
        );
      }
      const statusCode = result?.status_code;
      const isSuccess =
        statusCode === 201 ||
        statusCode === 200 ||
        Number(statusCode) === 201 ||
        Number(statusCode) === 200 ||
        (typeof result?.message === "string" &&
          /created successfully|success|updated successfully/i.test(
            result.message
          ));
      if (isSuccess) {
        toast.success(
          result?.message ??
            (editingInstitutionId
              ? "Institution updated."
              : "Institution created.")
        );
        handleModalOpenChange(false);
        fetchInstitutions();
      } else {
        toast.error(result?.message ?? "Failed to save institution");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Failed to save institution";
      toast.error(msg);
    }
  };

  const currentStepInfo = INSTITUTION_ONBOARDING_STEPS.find(
    s => s.step === step
  );
  const filteredInstitutions = institutions;

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-600 hover:bg-green-700">
            <CheckCircle className="mr-1 h-3 w-3" /> Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" /> Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" /> Pending
          </Badge>
        );
    }
  };

  // Use API statistics when available, else compute from list
  const totalCount = statistics?.total_institutions ?? institutions.length;
  const pendingCount =
    statistics?.pending_approval ??
    institutions.filter(i => i.status === "pending").length;
  const approvedCount =
    statistics?.approved ??
    institutions.filter(i => i.status === "approved").length;
  const rejectedCount =
    statistics?.rejected ??
    institutions.filter(i => i.status === "rejected").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Institution Management
          </h1>
          <p className="text-muted-foreground">
            Manage all institutions and their approvals
          </p>
        </div>
        <Button
          className="gap-2"
          onClick={() => {
            setEditingInstitutionId(null);
            setIsModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Institution
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Total Institutions
                </p>
                <p className="text-2xl font-bold">{totalCount}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Pending Approval
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {pendingCount}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {approvedCount}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  {rejectedCount}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search institutions..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Institutions Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            All Institutions ({filteredInstitutions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sr. No</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Trustee</TableHead>
                <TableHead>Status</TableHead>
                {isSuperAdmin && <TableHead>Highlighted</TableHead>}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInstitutions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isSuperAdmin ? 8 : 7}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No institutions found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInstitutions.map((inst, index) => (
                  <TableRow
                    key={inst.id}
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() =>
                      navigate(`/super-admin/institutions/${inst.id}`)
                    }
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell className="font-medium">{inst.name}</TableCell>
                    <TableCell className="capitalize">
                      {inst.type ?? "-"}
                    </TableCell>
                    <TableCell>
                      {[inst.city, inst.state].filter(Boolean).join(", ") ||
                        "-"}
                    </TableCell>
                    <TableCell>{inst.trustee_name ?? "-"}</TableCell>
                    <TableCell>{getStatusBadge(inst.status)}</TableCell>
                    {isSuperAdmin && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Switch
                          checked={Boolean(inst.is_highlighted)}
                          disabled={highlightUpdating === inst.id}
                          onCheckedChange={value =>
                            handleHighlightChange(inst, value)
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/super-admin/institutions/${inst.id}`);
                        }}
                        title="View Details"
                      >
                        <Eye className="mr-1 h-4 w-4" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingInstitutionId(inst.id);
                          setIsModalOpen(true);
                        }}
                        title="Edit Institution"
                      >
                        <Pencil className="mr-1 h-4 w-4" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={handleModalOpenChange}>
        <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pb-2 pt-6">
            <DialogTitle>
              {editingInstitutionId
                ? "Edit Institution"
                : "Add New Institution"}
            </DialogTitle>
            <p className="pt-3 text-sm text-muted-foreground">
              Step {step} of 5 — {currentStepInfo?.title ?? ""}
            </p>
            <div className="flex gap-2 pt-1">
              {INSTITUTION_ONBOARDING_STEPS.map(s => (
                <div
                  key={s.step}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    s.step <= step ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </DialogHeader>
          <div className="relative flex flex-1 flex-col overflow-y-auto px-6">
            {loadingFormData ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
              </div>
            ) : (
              <InstitutionOnboardingForm
                step={step}
                form={form}
                onNext={handleFormNext}
                onBack={handleFormBack}
                onFinalSubmit={handleFormFinalSubmit}
                selectedDepartments={selectedDepartments}
                setSelectedDepartments={setSelectedDepartments}
                selectedFacilities={selectedFacilities}
                setSelectedFacilities={setSelectedFacilities}
                selectedLevels={selectedLevels}
                setSelectedLevels={setSelectedLevels}
                institutionImages={institutionImages}
                setInstitutionImages={setInstitutionImages}
                existingImageUrls={existingImageUrls}
                onRemoveExistingImage={index =>
                  setExistingImageUrls(prev =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                isModal
                onCancel={() => handleModalOpenChange(false)}
                maxInstitutionImages={MAX_INSTITUTION_IMAGES}
                isEditMode={!!editingInstitutionId}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
